import {
  PIPE_GAP,
  PIPE_WIDTH,
  SCROLL_SPEED,
  VIRTUAL_HEIGHT,
  VIRTUAL_WIDTH,
} from "../../config";
import {
  type CharacterRender,
  type ExplosionRender,
  type HatRender,
  type TrailRender,
  getItem,
} from "../../../shared/shop-catalog";
import { Rng } from "../../utils/rng";
import { Background, paletteFor } from "../entities/Background";
import { drawCharacterSprite } from "../entities/CharacterSprite";
import { FlyFlock } from "../entities/FlyFlock";
import { Ground } from "../entities/Ground";
import { drawHatBack, drawHatFront } from "../entities/HatAccessory";
import { KiwiFlock } from "../entities/KiwiFlock";
import { Lizard } from "../entities/Lizard";
import { Explosion } from "../entities/Explosion";
import { PipePool } from "../entities/PipePool";
import { TrailRibbon, isParticleTrail } from "../entities/TrailRibbon";
import type { Button } from "../systems/Buttons";
import { drawIconButton } from "../systems/Buttons";
import { circleVsCircle, circleVsRect } from "../systems/Collision";
import {
  difficultyFor,
  difficultyForTime,
  isNightForScore,
  isNightForTime,
} from "../systems/Difficulty";
import { Particles } from "../systems/Particles";
import type { RaceRun, RaceSlot } from "../systems/Races";
import { fillRoundedRect } from "../../utils/draw";
import { lerp } from "../../utils/math";
import { GameOverScene } from "./GameOverScene";
import { drawMuteIcon } from "./MenuScene";
import { ModScene } from "./ModScene";
import { PauseScene } from "./PauseScene";
import type { GameContext, Scene } from "./Scene";

type Phase = "ready" | "playing" | "dying";

// 0.1% chance per solo run that a jumpscare fires mid-game.
const JUMPSCARE_CHANCE = 0.001;

export class PlayScene implements Scene {
  private readonly bg = new Background();
  private readonly ground = new Ground();
  private readonly kiwis = new KiwiFlock();
  private readonly flies = new FlyFlock();
  private readonly pipes: PipePool;
  private readonly lizard = new Lizard(VIRTUAL_WIDTH * 0.3, VIRTUAL_HEIGHT * 0.5);
  private readonly particles = new Particles();
  private readonly explosion = new Explosion();
  private phase: Phase = "ready";
  private deathTimer = 0;
  private trailAccum = 0;
  private readonly trailRibbon = new TrailRibbon();
  private spamFlapAccum = 0;
  // Jumpscare: armed once per run; fires once mid-game if the roll hits or the
  // mod flag is set. fire() itself skips the immune owner accounts.
  private jumpscareArmed = false;
  private jumpscareAt: number | null = null;
  private playElapsed = 0;

  // Ghost-race state (null for a normal solo run).
  private raceElapsed = 0;
  private raceSyncAccum = 0;
  private raceOpponent: RaceSlot | null = null;
  private raceGhostY = VIRTUAL_HEIGHT / 2;
  private raceDoneSent = false;
  private readonly ghostParticles = new Particles();
  private ghostTrailAccum = 0;
  private readonly ghostRibbon = new TrailRibbon();

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly battleId: string | null = null,
    private readonly race: RaceRun | null = null,
  ) {
    this.ctxMgr.score.reset();
    // Injected starting score/flies apply in races too — an admin-granted
    // head-start on the race scoreboard. Race difficulty ramps by elapsed time
    // (not score), so this doesn't touch the shared, seeded pipe stream.
    const startScore = this.ctxMgr.mods.consumeStartingScore();
    const startFlies = this.ctxMgr.mods.consumeStartingFlies();
    if (startScore > 0) this.ctxMgr.score.current = startScore;
    if (startFlies > 0) this.ctxMgr.score.flies = startFlies;
    if (race) {
      // Shared seed → identical pipe stream on both clients.
      this.pipes = new PipePool({ rng: new Rng(race.seed), scrollSpeed: SCROLL_SPEED, pipeGap: PIPE_GAP });
    } else {
      const initial = difficultyFor(this.ctxMgr.score.current);
      this.pipes = new PipePool({
        scrollSpeed: initial.scrollSpeed,
        pipeGap: initial.pipeGap,
      });
    }
    this.lizard.setSkin(
      this.ctxMgr.shop.getEquippedCharacterRender(),
      this.ctxMgr.shop.getEquippedHatRender(),
    );
  }

  enter(): void {
    this.ctxMgr.audio.music.setMode("jazz");

    // Pull the latest pushed mods right as the run starts so a prank an admin
    // queued while we were idle in a menu lands on this run, not 2.5s into it.
    if (this.ctxMgr.settings.hasName()) {
      void this.ctxMgr.mods.pollSelf(this.ctxMgr.settings.playerName);
    }

    const buttons: Button[] = [
      {
        x: 8,
        y: 8,
        w: 28,
        h: 28,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { x: 8, y: 8, w: 28, h: 28, draw: () => undefined, onPress: () => undefined },
            hit,
            drawPauseIcon,
          ),
        onPress: () => this.onPauseToggle(),
      },
      {
        x: VIRTUAL_WIDTH - 36,
        y: 8,
        w: 28,
        h: 28,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { x: VIRTUAL_WIDTH - 36, y: 8, w: 28, h: 28, draw: () => undefined, onPress: () => undefined },
            hit,
            (c, x, y) => drawMuteIcon(c, x, y, this.ctxMgr.settings.muted),
          ),
        onPress: () => {
          const muted = this.ctxMgr.settings.toggleMuted();
          this.ctxMgr.audio.setMuted(muted);
        },
      },
    ];

    if (this.ctxMgr.mods.isAdmin(this.ctxMgr.settings.playerName) || this.ctxMgr.shop.isAdmin()) {
      buttons.push({
        x: 8,
        y: 44,
        w: 28,
        h: 28,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { x: 8, y: 44, w: 28, h: 28, draw: () => undefined, onPress: () => undefined },
            hit,
            drawModIcon,
          ),
        onPress: () => this.openModMenu(),
      });
    }

    this.ctxMgr.buttons.set(buttons);
  }

  private openModMenu(): void {
    if (this.ctxMgr.mods.ensureUnlocked()) {
      // Pass `this` so ModScene's back button returns to this PlayScene
      // instance — its lizard position, score, and pipe state are preserved.
      this.ctxMgr.goTo(new ModScene(this.ctxMgr, this));
    }
  }

  exit(): void {
    this.ctxMgr.buttons.clear();
  }

  onPauseToggle(): void {
    if (this.phase !== "playing") return;
    if (this.race) return; // pausing would desync a live race
    this.ctxMgr.goTo(new PauseScene(this.ctxMgr, this));
  }

  onFlap(): void {
    // In a race the run starts on the shared countdown, not on a flap.
    if (this.phase === "ready" && !this.race) {
      this.phase = "playing";
    }
    if (this.phase === "playing") {
      const flags = this.ctxMgr.mods.flags;
      this.lizard.flap();
      const flapCount = flags.earrapeFlap ? 6 : 1;
      for (let i = 0; i < flapCount; i += 1) this.ctxMgr.audio.play("flap");
      this.particles.emitPuff(this.lizard.x - 8, this.lizard.y + 4, 5);
    }
  }

  update(dt: number): void {
    this.ctxMgr.effects.tick(dt);

    // A race starts on the shared countdown; mods are ignored for fairness.
    if (this.race && this.phase === "ready" && Date.now() >= this.race.startAt) {
      this.phase = "playing";
    }

    if (this.race) {
      if (this.raceOpponent?.y != null) {
        this.raceGhostY = lerp(this.raceGhostY, this.raceOpponent.y, 0.3);
      }
    }

    // Pranks (grief flags) apply in races too — they only sabotage the local
    // victim's run; the opponent renders from its own synced snapshot. Legacy
    // mods (noclip, injected score/flies) stay race-disabled for fairness.
    const flags = this.ctxMgr.mods.flags;
    this.lizard.setMods(flags);
    if (flags.shakeyCamera) this.ctxMgr.effects.shake(0.25);
    if (flags.spamFlapSound) {
      this.spamFlapAccum += dt;
      if (this.spamFlapAccum >= 0.12) {
        this.ctxMgr.audio.play("flap");
        this.spamFlapAccum = 0;
      }
    }

    const score = this.ctxMgr.score.current;

    let baseDiff;
    if (this.race) {
      if (this.phase === "playing") this.raceElapsed += dt;
      baseDiff = difficultyForTime(this.raceElapsed);
    } else {
      baseDiff = difficultyFor(score);
    }
    const night = this.isNightNow(); // after raceElapsed is advanced this frame
    // Scroll speed is constant (no difficulty speed-up); only the pipe gap keeps
    // narrowing. The opt-in "Speed Up"/"slippery air" mods still scale it live.
    let scrollSpeed = baseDiff.scrollSpeed;
    if (flags.speedUp) scrollSpeed *= 1.8;
    if (flags.slipperyAir) scrollSpeed *= 1.25;
    const diff = { scrollSpeed, pipeGap: baseDiff.pipeGap };
    this.pipes.scrollSpeed = scrollSpeed;
    this.pipes.pipeGap = baseDiff.pipeGap;
    this.ctxMgr.audio.music.setMode(night ? "rock" : "jazz");

    this.bg.update(dt, diff.scrollSpeed);
    this.kiwis.update(dt, this.bg);

    if (this.phase === "ready") {
      this.lizard.update(dt);
      this.ground.update(dt, diff.scrollSpeed);
      return;
    }

    if (this.ctxMgr.effects.isPaused()) {
      this.particles.update(dt);
      return;
    }

    if (this.phase === "dying") {
      this.lizard.update(dt);
      this.particles.update(dt);
      this.explosion.update(dt);
      this.deathTimer -= dt;
      if (this.deathTimer <= 0) {
        const result = this.ctxMgr.score.finalize();
        this.ctxMgr.goTo(new GameOverScene(this.ctxMgr, result, this.battleId, this.race));
      }
      return;
    }

    this.updateJumpscare(dt);

    this.lizard.update(dt);
    this.pipes.update(dt);
    this.ground.update(dt, diff.scrollSpeed);
    if (!this.race) {
      // flies (bonus) are off in races — they'd diverge per client.
      this.flies.update(dt, diff.scrollSpeed, this.pipes.pipes);
    }
    this.updateTrail(dt, diff.scrollSpeed);
    this.particles.update(dt);
    if (this.race) {
      this.syncRace(dt);
      this.updateGhostTrail(dt, diff.scrollSpeed);
    }

    const groundY = this.ground.topY();
    const liz = { x: this.lizard.x, y: this.lizard.y, r: this.lizard.hitboxRadius() };
    const noclip = this.ctxMgr.mods.noclip;

    if (noclip) {
      if (this.lizard.y + liz.r > groundY) {
        this.lizard.y = groundY - liz.r;
        if (this.lizard.vy > 0) this.lizard.vy = 0;
      }
      if (this.lizard.y - liz.r < 0) {
        this.lizard.y = liz.r;
        if (this.lizard.vy < 0) this.lizard.vy = 0;
      }
    } else if (this.lizard.y + liz.r >= groundY || this.lizard.y - liz.r <= 0) {
      this.die();
      return;
    }

    for (const p of this.pipes.active()) {
      if (!noclip && (circleVsRect(liz, p.topRect()) || circleVsRect(liz, p.bottomRect(groundY)))) {
        this.die();
        return;
      }
      if (!p.passed && p.x + PIPE_WIDTH < this.lizard.x) {
        p.passed = true;
        this.ctxMgr.score.increment();
        this.ctxMgr.audio.play("score");
      }
    }

    for (const f of this.flies.active()) {
      if (circleVsCircle(liz, { x: f.x, y: f.y, r: f.r })) {
        f.consume();
        this.ctxMgr.score.collectFly();
        this.ctxMgr.audio.play("lick");
        this.particles.emitPuff(f.x, f.y, 4);
      }
    }
  }

  // Race-aware day/night phase: time-based during ghost races (so both clients
  // agree and the night-gated speed-up stays deterministic), score-based otherwise.
  private isNightNow(): boolean {
    return this.race
      ? isNightForTime(this.raceElapsed)
      : isNightForScore(this.ctxMgr.score.current);
  }

  // Arm once per run, then fire a single jumpscare a few seconds in if the random
  // roll hit (solo only). A mod-pushed jumpscare is handled separately — it fires on
  // receipt (Game.pollMods), not here. The immune accounts are filtered in fire().
  private updateJumpscare(dt: number): void {
    if (!this.jumpscareArmed) {
      this.jumpscareArmed = true;
      const rolled = !this.race && Math.random() < JUMPSCARE_CHANCE;
      if (rolled) this.jumpscareAt = 1 + Math.random() * 5; // 1–6s into the run
    }
    if (this.jumpscareAt === null) return;
    this.playElapsed += dt;
    if (this.playElapsed >= this.jumpscareAt) {
      this.jumpscareAt = null;
      this.ctxMgr.jumpscare.fire();
    }
  }

  private die(): void {
    this.phase = "dying";
    this.lizard.kill();
    this.ground.scrolling = false;
    this.deathTimer = 0.6;
    this.explosion.spawn(
      this.lizard.x,
      this.lizard.y,
      this.ctxMgr.shop.getEquippedExplosionRender() ?? DEFAULT_EXPLOSION,
    );
    this.ctxMgr.audio.play("hit");
    this.ctxMgr.audio.play("die");
    this.ctxMgr.effects.shake(0.7);
    this.ctxMgr.effects.triggerHitStop();
    if (this.race) this.postRace(true);
  }

  private syncRace(dt: number): void {
    this.raceSyncAccum += dt;
    if (this.raceSyncAccum < 0.15) return;
    this.raceSyncAccum = 0;
    this.postRace(false);
  }

  /** Push my position to the room and pick up the opponent's latest snapshot. */
  private postRace(done: boolean): void {
    if (!this.race || (done && this.raceDoneSent)) return;
    if (done) this.raceDoneSent = true;
    const { playerName, authToken } = this.ctxMgr.settings;
    const shop = this.ctxMgr.shop;
    void this.ctxMgr.races
      .pos(playerName, authToken, this.race.id, {
        y: this.lizard.y,
        score: this.ctxMgr.score.current,
        alive: !done,
        done,
        cosmetics: {
          char: shop.equippedId("character"),
          hat: shop.equippedId("hat"),
          trail: shop.equippedId("trail"),
        },
      })
      .then((st) => {
        if (st) this.raceOpponent = st.other;
      });
  }

  // Extend the opponent's trail behind their ghost while they're alive — particle
  // trails as particles, the rest as a ribbon (mirrors the local player).
  private updateGhostTrail(dt: number, scrollSpeed: number): void {
    const opp = this.raceOpponent;
    const alive = !!(opp && opp.alive && opp.y !== null);
    const cfg = this.ghostTrailRender();
    if (cfg && isParticleTrail(cfg.kind)) {
      this.ghostParticles.update(dt);
      if (alive) {
        const interval = 1 / Math.max(1, cfg.ratePerSec);
        this.ghostTrailAccum += dt;
        while (this.ghostTrailAccum >= interval) {
          this.ghostParticles.emitTrail(this.lizard.x - 10, this.raceGhostY + 2, cfg);
          this.ghostTrailAccum -= interval;
        }
      } else {
        this.ghostTrailAccum = 0;
      }
      return;
    }
    this.ghostTrailAccum = 0;
    this.ghostRibbon.update(dt, scrollSpeed);
    if (alive) this.ghostRibbon.push(this.lizard.x - 10, this.raceGhostY + 2);
  }

  private ghostCharRender(): CharacterRender {
    const id = this.raceOpponent?.cosmetics?.char;
    const item = id ? getItem(id) : null;
    return item && item.slot === "character" ? item.render : GHOST_SKIN;
  }

  private ghostHatRender(): HatRender | null {
    const id = this.raceOpponent?.cosmetics?.hat;
    const item = id ? getItem(id) : null;
    return item && item.slot === "hat" ? item.render : null;
  }

  private ghostTrailRender(): TrailRender | null {
    const id = this.raceOpponent?.cosmetics?.trail;
    const item = id ? getItem(id) : null;
    return item && item.slot === "trail" ? item.render : null;
  }

  private renderPipesWithMods(
    ctx: CanvasRenderingContext2D,
    flags: import("../../../shared/mods").ModFlags,
  ): void {
    const groundY = this.ground.topY();
    if (flags.invisibleObstacles) return;
    if (flags.tinyPipes) {
      ctx.fillStyle = "#5a3a22";
      for (const p of this.pipes.active()) {
        const top = p.topRect();
        const bot = p.bottomRect(groundY);
        const cx = top.x + top.w / 2;
        ctx.fillRect(cx - 3, top.y, 6, top.h);
        ctx.fillRect(cx - 3, bot.y, 6, bot.h);
      }
      return;
    }
    this.pipes.render(ctx, groundY);
    if (flags.discoPipes) {
      const hue = (performance.now() / 8) % 360;
      ctx.fillStyle = `hsla(${hue},80%,55%,0.45)`;
      for (const p of this.pipes.active()) {
        const top = p.topRect();
        const bot = p.bottomRect(groundY);
        ctx.fillRect(top.x, top.y, top.w, top.h);
        ctx.fillRect(bot.x, bot.y, bot.w, bot.h);
      }
    }
  }

  // Particle-type trails (confetti, snow, stars…) keep spraying particles; every
  // other trail follows the sprite as a single continuous ribbon.
  private updateTrail(dt: number, scrollSpeed: number): void {
    const cfg = this.ctxMgr.shop.getEquippedTrailRender();
    if (cfg && isParticleTrail(cfg.kind)) {
      const interval = 1 / Math.max(1, cfg.ratePerSec);
      this.trailAccum += dt;
      while (this.trailAccum >= interval) {
        this.particles.emitTrail(this.lizard.x - 10, this.lizard.y + 2, cfg);
        this.trailAccum -= interval;
      }
      return;
    }
    this.trailAccum = 0;
    this.trailRibbon.update(dt, scrollSpeed);
    this.trailRibbon.push(this.lizard.x - 10, this.lizard.y + 2);
  }

  render(ctx: CanvasRenderingContext2D, alpha: number): void {
    const flags = this.ctxMgr.mods.flags;
    const night = this.isNightNow();
    const palette = paletteFor(
      this.ctxMgr.shop.getEquippedBackgroundRender(false),
      this.ctxMgr.shop.getEquippedBackgroundRender(true),
      night,
    );
    const offset = this.ctxMgr.effects.shakeOffset();
    ctx.save();
    ctx.translate(offset.x, offset.y);

    this.bg.render(ctx, palette);
    this.kiwis.render(ctx, this.bg, palette.isNight);
    this.renderPipesWithMods(ctx, flags);
    this.flies.render(ctx);
    this.ground.render(ctx);
    this.particles.render(ctx);
    const trailCfg = this.ctxMgr.shop.getEquippedTrailRender();
    if (trailCfg && !isParticleTrail(trailCfg.kind)) this.trailRibbon.render(ctx, trailCfg);
    if (this.race) this.renderGhost(ctx);
    this.lizard.render(ctx, alpha);

    if (flags.blindMode) {
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.92)";
      ctx.beginPath();
      ctx.rect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
      ctx.arc(this.lizard.x, this.lizard.y, 55, 0, Math.PI * 2);
      ctx.fill("evenodd");
      ctx.restore();
    }

    ctx.restore();

    const flash = this.ctxMgr.effects.flash();
    if (flash > 0) {
      ctx.fillStyle = `rgba(255,255,255,${flash * 0.6})`;
      ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
    }

    // The death boom draws on TOP of the hit-stop flash (and shares the shake
    // offset) so it's unmistakable at the moment of impact.
    ctx.save();
    ctx.translate(offset.x, offset.y);
    this.explosion.render(ctx);
    ctx.restore();

    if (this.phase === "ready") {
      ctx.fillStyle = "rgba(26,31,43,0.9)";
      ctx.textAlign = "center";
      if (this.race) {
        const left = Math.max(0, this.race.startAt - Date.now());
        const n = Math.ceil(left / 1000);
        ctx.font = "bold 48px system-ui, sans-serif";
        ctx.fillText(n > 0 ? String(n) : "GO!", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT * 0.32);
        ctx.font = "14px system-ui, sans-serif";
        ctx.fillText(`racing ${this.race.opponent}`, VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT * 0.32 + 30);
      } else {
        ctx.font = "bold 22px system-ui, sans-serif";
        ctx.fillText("get ready", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT * 0.3);
        ctx.font = "14px system-ui, sans-serif";
        ctx.fillText("tap to flap", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT * 0.3 + 24);
      }
    } else {
      ctx.fillStyle = "#fff";
      ctx.strokeStyle = "#1a1f2b";
      ctx.lineWidth = 4;
      ctx.font = "bold 36px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.strokeText(String(this.ctxMgr.score.current), VIRTUAL_WIDTH / 2, 70);
      ctx.fillText(String(this.ctxMgr.score.current), VIRTUAL_WIDTH / 2, 70);
    }

    if (this.race) this.renderRaceHud(ctx);
    else drawFlyHud(ctx, this.ctxMgr.score.flies);
  }

  private renderGhost(ctx: CanvasRenderingContext2D): void {
    const opp = this.raceOpponent;
    if (!opp || opp.y === null) return;

    // The opponent's trail — particles or a continuous ribbon, matching its kind.
    const ghostCfg = this.ghostTrailRender();
    if (ghostCfg && isParticleTrail(ghostCfg.kind)) this.ghostParticles.render(ctx);
    else if (ghostCfg) this.ghostRibbon.render(ctx, ghostCfg);

    // The opponent's lizard, drawn semi-transparent as a ghost.
    const t = performance.now() / 1000;
    const hat = this.ghostHatRender();
    const anim = { propAngle: t * 12, bobTime: t, flapTime: 0 };
    ctx.save();
    ctx.globalAlpha = opp.alive ? 0.55 : 0.25;
    ctx.translate(this.lizard.x, this.raceGhostY);
    if (hat) drawHatBack(ctx, 14, hat, anim);
    drawCharacterSprite(ctx, 14, this.ghostCharRender(), { bobTime: t, flapTime: 0 });
    if (hat) drawHatFront(ctx, 14, hat, anim);
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  private renderRaceHud(ctx: CanvasRenderingContext2D): void {
    const opp = this.raceOpponent;
    const oppScore = opp ? opp.score : 0;
    const me = this.ctxMgr.score.current;
    const text = `You ${me}   ${this.race?.opponent ?? "Opp"} ${oppScore}`;
    ctx.save();
    ctx.font = "bold 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    const w = ctx.measureText(text).width + 16;
    const x = (VIRTUAL_WIDTH - w) / 2;
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    fillRoundedRect(ctx, x, 4, w, 20, 6);
    ctx.fillStyle = me >= oppScore ? "#9ad36a" : "#ff8a8a";
    ctx.textBaseline = "middle";
    ctx.fillText(text, VIRTUAL_WIDTH / 2, 14);
    ctx.textBaseline = "alphabetic";
    ctx.restore();
  }
}

// Played on death for everyone who hasn't equipped a custom/owned explosion —
// a classic fiery boom so a hit always reads.
const DEFAULT_EXPLOSION: ExplosionRender = {
  kind: "fireball",
  color: "#ff7043",
  color2: "#ffd54a",
};

const GHOST_SKIN = {
  kind: "lizard" as const,
  body: "#9fb0c0",
  belly: "#dfe7ee",
  tail: "#6f8090",
  legs: "#56606c",
};

function drawFlyHud(ctx: CanvasRenderingContext2D, flies: number): void {
  const boxW = 56;
  const boxH = 20;
  const x = (VIRTUAL_WIDTH - boxW) / 2;
  const y = 4;

  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  fillRoundedRect(ctx, x, y, boxW, boxH, 6);

  // tiny fly icon
  const fx = x + 10;
  const fy = y + boxH / 2;
  ctx.fillStyle = "rgba(220,230,255,0.7)";
  ctx.beginPath();
  ctx.ellipse(fx - 1.5, fy - 1, 3, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(fx + 1.5, fy - 1, 3, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#1a1a1a";
  ctx.beginPath();
  ctx.ellipse(fx, fy, 2.4, 1.6, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#f0e0a8";
  ctx.font = "12px ui-monospace, monospace";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(`× ${flies}`, x + 20, y + boxH / 2);
  ctx.restore();
}

function drawPauseIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = "#fff";
  ctx.fillRect(cx - 5, cy - 6, 3, 12);
  ctx.fillRect(cx + 2, cy - 6, 3, 12);
}

function drawModIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = "#fff";
  ctx.font = "bold 14px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("M", cx, cy + 1);
  ctx.textBaseline = "alphabetic";
}
